import { log } from './Observable.js';
function notify(sender, value, cb) {
    try {
        cb(value, sender);
    }
    catch (e) {
        log.error('Observable', sender, 'callback', cb, 'threw', e);
    }
}
/**
 * A transaction that delays all emits until it finalizes.
 */
class ExplicitTransaction {
    constructor() {
        this.pending = new Map();
    }
    /**
     * Finalizes the transactions by emitting all pending values.
     *
     * Note that pending emits may cause more emits to be queued up.
     * Thus, it is still possible for intermediate values to be emitted from downstream observables when the dependency path from the changed sources to the target doesn’t have the same length.
     *
     * FIXME: Address this by letting the transaction know about the dependency graph.
     */
    finalize() {
        const pending = this.pending;
        this.pending = new Map();
        for (const [sender, map] of pending) {
            for (const [cb, value] of map) {
                notify(sender, value, cb);
            }
        }
        if (this.pending.size > 0) {
            // Recurse to resolve dependencies
            this.finalize();
        }
    }
    enqueue(sender, value, cbs) {
        let map = this.pending.get(sender);
        if (!map) {
            map = new Map();
            this.pending.set(sender, map);
        }
        for (const cb of cbs) {
            map.set(cb, value);
        }
    }
}
/**
 * Execute all emits immediately.
 * Used when no transaction is open.
 */
class ImplicitTransaction {
    enqueue(sender, value, cbs) {
        for (const cb of cbs) {
            notify(sender, value, cb);
        }
    }
}
let currentTransaction;
const implicit = new ImplicitTransaction();
/**
 * Executes the given block inside a transaction.
 * This means emits will be postponed until after the block has executed.
 */
export function withTransaction(operations) {
    if (currentTransaction) {
        operations();
        return;
    }
    currentTransaction = new ExplicitTransaction();
    operations();
    currentTransaction.finalize();
    currentTransaction = undefined;
}
/**
 * @returns the currently active transaction to use when emitting values.
 */
export function current() {
    if (currentTransaction) {
        return currentTransaction;
    }
    return implicit;
}
